// stores/auth.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authRepository } from '~/utils/authRepository'
import { useNuxtApp } from '#app'

/**
 * Returns a promise that resolves after the specified milliseconds.
 * @param ms Number of milliseconds to wait.
 */
const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms))

export const useAuthStore = defineStore('auth', () => {
  const codeToken = ref<string | null>(null)
  const user = ref<any>(null)
  const mobile = ref<any>(null)

  const setCodeToken = (token: string) => {
    codeToken.value = token

    const codeTokenCookie = useCookie('code_token', {
      // هذه الخيارات ستعمل عند القراءة من الخادم، ولكن تعيين httpOnly يتم فقط من الخادم
      secure: process.env.NODE_ENV === "production",
      sameSite: 'strict',
      path: '/'
    })
    
    codeTokenCookie.value = token
  }
  
  const setMobile = (phone: string) => {
    mobile.value = phone
  }

  const setUser = (userData: any) => {
    user.value = userData

    const userCookie = useCookie('user', {
      // هذه الخيارات ستعمل عند القراءة من الخادم، ولكن تعيين httpOnly يتم فقط من الخادم
      secure: process.env.NODE_ENV === "production",
      sameSite: 'strict',
      path: '/'
    })
    
    userCookie.value = JSON.stringify(userData)


    if (userData.token) {

      
      const authCookie = useCookie('api_token', {
        // هذه الخيارات ستعمل عند القراءة من الخادم، ولكن تعيين httpOnly يتم فقط من الخادم
        secure: process.env.NODE_ENV === "production",
        sameSite: 'strict',
        path: '/'
      })
      
      authCookie.value = userData.token

    }
  }

  /**
   * Loads the user from localStorage and then refreshes the user data from the API.
   */
  const loadUserFromStorage = async () => {

    if (import.meta.client) {
      const storedUser = useCookie('user');
      // 1. Grab the saved timestamp (or 0 if never set)
      const lastInteraction = useCookie<number>(
        'lastAppInteractionTimestamp',
        { default: () => 0 }
      )
      if (storedUser.value && storedUser.value !== 'undefined') {
        setUser(storedUser.value)
        try {
          
          // Wait for 1 second before fetching the fresh user profile
          await delay(1000)
          const { $api } = useNuxtApp() // Ensure $api is provided via your Nuxt plugin
          // Get repository instance using the provided $api instance
          const { getProfile,setAppInteraction } = authRepository($api)
          const freshUser = await getProfile()
          if (freshUser) {
            setUser(freshUser.data)
          }
          await delay(1000)


          
        // 2. Compute how many milliseconds have passed since then
        const msSinceLast = Date.now() - (lastInteraction.value || 0)

        // 3. If it’s been more than 10 minutes (10 * 60 * 1000 ms), fire and update the cookie
        if (lastInteraction.value == 0 || msSinceLast > 10 * 60 * 1000) {
          const { response } = await authRepository().setAppInteraction()
          if (response.status) {
            lastInteraction.value = Date.now()
          }
        }
          // run this if lastAppInteractionTimestamp.value < 10 munits 
        } catch (error) {
          console.error('Failed to load user:', error)
        }
      }
    }
  
  }

  const logout = () => {
    
    user.value = null

    const userCookie = useCookie('user', {
      // هذه الخيارات ستعمل عند القراءة من الخادم، ولكن تعيين httpOnly يتم فقط من الخادم
      secure: process.env.NODE_ENV === "production",
      sameSite: 'strict',
      path: '/'
    })
    
    userCookie.value = null

    const code_tokenCookie = useCookie('code_token', {
      // هذه الخيارات ستعمل عند القراءة من الخادم، ولكن تعيين httpOnly يتم فقط من الخادم
      secure: process.env.NODE_ENV === "production",
      sameSite: 'strict',
      path: '/'
    })
    
    code_tokenCookie.value = null

    const api_tokenCookie = useCookie('api_token', {
      // هذه الخيارات ستعمل عند القراءة من الخادم، ولكن تعيين httpOnly يتم فقط من الخادم
      secure: process.env.NODE_ENV === "production",
      sameSite: 'strict',
      path: '/'
    })
    
    api_tokenCookie.value = null
  }

  const isAuthenticated = computed(() => !!user.value)

  // Optionally, load the user during store initialization on the client.
  // if (import.meta.client) {
  //   loadUserFromStorage()
  // }

  return {
    codeToken,
    user,
    mobile,
    setCodeToken,
    setMobile,
    setUser,
    loadUserFromStorage,
    logout,
    isAuthenticated,
  }
})
